/**
 * useLivePatternStats — polling wrapper around `analyzePatternForwardStats`.
 *
 * Surfaces, in real time, the patterns that are firing RIGHT NOW on the
 * most recent closed 5m bar, plus the historical follow-through probability
 * of each — computed from today's bars (cheap, single-day scope).
 *
 * Pain point this addresses (user, 2026-05-18):
 *   "There's so much data. Like what's the probability we bounce the 5M
 *    09EMA after rejecting off the Golden Crayon? The 15M 09EMA, 21EMA…"
 *
 * Same instrument resolution + day gating as useCounterBiasWhisper. Polls
 * via `barDataService.fetchBars` (cached), so two hooks polling the same
 * symbol don't duplicate disk I/O — the second one hits cache.
 *
 * What this hook does NOT do (yet):
 *   - Multi-day historical aggregation. Today only for v1. Future Wave G
 *     phase can extend to cross-day stats by scanning the trader's full
 *     cached bar history.
 *   - Per-trade outcome tracking (R-multiples, target hits). Returns
 *     pure bar-relative outcomes: follow-through close + EMA tags.
 */

import { useEffect, useMemo, useState } from 'react';
import { barDataService } from '../services/barData';
import {
  analyzePatternForwardStats,
  DEFAULT_OUTCOMES,
  isActionablePattern,
} from '../services/coach/patternForwardStats';
import { detectSetupPatterns } from '../services/barAnalysis/setupPatterns';
import { computeBarFreshness } from '../services/barAnalysis/barFreshness';

const POLL_INTERVAL_MS = 60 * 1000;

/**
 * @param {Object} params
 * @param {string} params.symbol             Symbol to poll bars for.
 * @param {string} params.tradingDay         Date key (YYYY-MM-DD) of bars to fetch.
 * @param {string} params.currentTradingDay  Today's trading day.
 * @param {boolean} [params.enabled=true]    Master switch.
 *
 * @returns {Object}
 *   {
 *     activeFires,   // Array of patterns firing on the most recent CLOSED bar
 *                    //   [{ patternType, direction, label, score?, ... }]
 *     stats,         // analyzePatternForwardStats output for today's bars
 *     barTime,       // Unix sec of the bar `activeFires` was evaluated against
 *     barPrice,      // Close of that bar
 *     isPolling,
 *   }
 */
export const useLivePatternStats = ({
  symbol,
  tradingDay,
  currentTradingDay,
  enabled = true,
} = {}) => {
  const [activeFires, setActiveFires] = useState([]);
  const [stats, setStats] = useState(null);
  const [barTime, setBarTime] = useState(null);
  const [barPrice, setBarPrice] = useState(null);
  const [freshness, setFreshness] = useState(null);
  const [isPolling, setIsPolling] = useState(false);

  const shouldPoll = Boolean(
    enabled &&
    symbol &&
    tradingDay &&
    currentTradingDay &&
    tradingDay === currentTradingDay
  );

  // Reset state on context change so a symbol switch doesn't show stale fires
  // from the previous symbol while the next poll is in flight.
  const contextKey = useMemo(() => `${tradingDay || ''}::${symbol || ''}`, [tradingDay, symbol]);
  useEffect(() => {
    setActiveFires([]);
    setStats(null);
    setBarTime(null);
    setBarPrice(null);
    setFreshness(null);
  }, [contextKey]);

  useEffect(() => {
    if (!shouldPoll) {
      setIsPolling(false);
      return undefined;
    }
    setIsPolling(true);

    let cancelled = false;

    const pollOnce = async () => {
      let bars = null;
      try {
        const res = await barDataService.fetchBars(
          { symbol, dateStr: tradingDay, timeframe: '5m' },
          { skipCache: false }
        );
        bars = res?.bars || null;
      } catch {
        return;
      }
      if (cancelled) return;

      // Compute freshness unconditionally so the UI can render "feed down"
      // even when bars are missing or too few to analyze.
      const fresh = computeBarFreshness({ bars: bars || [] });
      setFreshness(fresh);

      // Early-session gate (v7.6.0 fix): only bail when bars are completely
      // unavailable. The previous `< 30` gate hid fires for the first ~2.5
      // hours of every session — at 7:23pm ET (83 min after the 6pm open),
      // only ~16 5m bars have closed and the panel silently showed "No
      // setups firing" even when short-lookback detectors (signal-bar,
      // 3-bar-play, h2/l2) would have fired.
      //
      // Each detector enforces its own warmup internally:
      //   - signal-bar:  1 bar
      //   - 3-bar-play:  3 bars
      //   - h2 / l2:     entryIdx >= 10 (~50 min into session)
      //   - uno-reverse: ~10 bars
      //   - holy-grail:  ~25 bars (20 EMA + ADX warmup)
      //   - wolf-stack:  ~50 bars (EMA-50 warmup)
      // Long-lookback detectors return null safely until enough history
      // accumulates. `analyzePatternForwardStats` is internally safe at
      // low bar counts — it returns an empty `results: {}` when
      // `safeFrom > safeTo`, which the UI renders as "First fire today —
      // no stats yet" per pattern. Better to show a fire without stats
      // than to hide it entirely.
      if (!Array.isArray(bars) || bars.length === 0) return;

      // Identify the most recent closed bar (last in array).
      const lastBar = bars[bars.length - 1];
      const ts = Number(lastBar?.time);
      const price = Number(lastBar?.close);
      if (!Number.isFinite(ts) || !Number.isFinite(price)) return;

      // What's firing on this bar (both directions)?
      const currentFires = [];
      for (const dir of ['long', 'short']) {
        let patterns = [];
        try {
          patterns = detectSetupPatterns({
            bars,
            entryTs: ts,
            entryPrice: price,
            direction: dir,
          });
        } catch {
          patterns = [];
        }
        for (const p of patterns) {
          if (!isActionablePattern(p)) continue;
          currentFires.push({ ...p, direction: dir });
        }
      }

      // Compute today's bar-level forward stats (cheap on one day's bars).
      let statsResult = null;
      try {
        statsResult = analyzePatternForwardStats({
          bars,
          outcomes: DEFAULT_OUTCOMES,
        });
      } catch {
        statsResult = null;
      }

      if (cancelled) return;

      setActiveFires(currentFires);
      setStats(statsResult);
      setBarTime(ts);
      setBarPrice(price);
    };

    pollOnce();
    const id = setInterval(pollOnce, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
      setIsPolling(false);
    };
  }, [shouldPoll, symbol, tradingDay, contextKey]);

  return { activeFires, stats, barTime, barPrice, isPolling, freshness };
};

export default useLivePatternStats;
